import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { ChatGroupInterface } from '../interfaces/chatMessageInterface';
import LocalDbService, { MESSAGE_GROUP_TABLE_NAME } from './localDbService';

interface MessageGroupCacheServiceProps {
  organizationId: string | undefined;
}

interface DbRow {
  id: string;
  value: ChatGroupInterface;
  createdAt?: string;
}

let singleton: MessageGroupCacheService | undefined;
let organizationId: string | undefined;

const toTimestamp = (value?: string | null) => (value ? new Date(value).getTime() : 0);

// Same order as the server conversation list: pinned first by order, then most recent activity.
export const sortConversations = <T extends Pick<ChatGroupInterface, 'pinned' | 'order' | 'lastMessageTime'>>(
  groups: T[]
) =>
  [...groups].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    if (a.pinned && b.pinned) return (a.order ?? 0) - (b.order ?? 0);
    return toTimestamp(b.lastMessageTime) - toTimestamp(a.lastMessageTime);
  });

/**
 * Cached conversation list (lean summaries, no messages) for instant sidebar paint and member
 * lookups such as resolving an existing DM.
 */
export default class MessageGroupCacheService extends LocalDbService {
  constructor(props: MessageGroupCacheServiceProps) {
    const db_name = `${props.organizationId}`;
    const serviceProps = { ...props, dbName: db_name, tableName: MESSAGE_GROUP_TABLE_NAME };
    super(serviceProps);
  }

  static getInstance(): MessageGroupCacheService {
    const company = UserPreferenceSingleton.getInstance().getWorkspace();
    if (company && (!singleton || organizationId !== company.id)) {
      organizationId = company?.id;
      singleton = new MessageGroupCacheService({ organizationId: company?.id });
    }
    return singleton!;
  }

  static removeInstance() {
    if (singleton) singleton.closeDb();
    organizationId = undefined;
    singleton = undefined;
  }

  async getMessageGroups(): Promise<ChatGroupInterface[]> {
    if (this.db) {
      const rows: DbRow[] = await this.getDb().toArray();
      return sortConversations(rows.map((row) => row.value).filter((group) => group));
    }
    return [];
  }

  async setMessageGroups(value: ChatGroupInterface[]) {
    if (!value || !this.db) return false;
    await this.initializeDatabaseConnection();
    // IndexedDB stores only structured-cloneable data; rows built from store models carry MobX
    // observables, so store a plain copy.
    const plain: ChatGroupInterface[] = JSON.parse(JSON.stringify(value));
    // Atomic replace: readers (e.g. existing-DM lookup) must never see an empty list mid-write.
    return this.db.transaction('rw', this.getDb(), async () => {
      await this.getDb().clear();
      return this.getDb().bulkPut(this.getMappedValues(plain));
    });
  }
}
